import React, { useState, useCallback, useEffect } from 'react';
import { collection, addDoc, getDocs, query, where, orderBy, updateDoc, doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { WeeklyTimesheet, WeeklyTimesheetEntry, TimesheetImportData, TimesheetRateAnalysis } from '../types/timesheet.ts';
import { Employee } from '../types/employee.ts';
import { Upload, Download, FileText, ArrowRight, CheckCircle, AlertCircle, Calendar, TrendingUp } from 'lucide-react';
import { analyzeTimesheetRates, analyzeCompleteTimesheetRates, generateRateAnalysisSummary } from '../utils/rateComparison.ts';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

const TimesheetImport: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [contractors, setContractors] = useState<any[]>([]);
  const [selectedContractor, setSelectedContractor] = useState<{id: string, name: string, markupPercentage: number} | null>(null);
  const [importedData, setImportedData] = useState<TimesheetImportData[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [excelHeaders, setExcelHeaders] = useState<string[]>([]);
  const [showMapping, setShowMapping] = useState(false);
  const [columnExamples, setColumnExamples] = useState<any>({});
  const [autoSplitJobName, setAutoSplitJobName] = useState(false);
  const [employeeNameMappings, setEmployeeNameMappings] = useState<{[key: string]: string}>({});
  const [unmatchedEmployees, setUnmatchedEmployees] = useState<string[]>([]);
  const [showEmployeeMapping, setShowEmployeeMapping] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [rateAnalysis, setRateAnalysis] = useState<TimesheetRateAnalysis | null>(null);
  const [showRateAnalysis, setShowRateAnalysis] = useState(false);
  const [showCreateEmployeeModal, setShowCreateEmployeeModal] = useState(false);
  const [createEmployeeData, setCreateEmployeeData] = useState<{firstName: string, lastName: string} | null>(null);
  
  // Import settings
  const [importSettings, setImportSettings] = useState({
    jobName: '',
    jobNumber: '',
    weekEnding: getDefaultWeekEnding(),
    createSeparateTimesheets: true
  });

  // Column mapping for timesheet data
  const [columnMapping, setColumnMapping] = useState({
    firstName: '',
    lastName: '',
    employeeName: '', // Alternative to firstName/lastName
    employeeCode: '', // Employee ID/Code
    date: '', // Date field to determine day of week
    hours: '', // Hours worked on that day
    payType: '', // Regular/Overtime designation
    activityCode: '',
    activityDescription: '',
    jobName: '', // If each row has job info
    jobNumber: '', // If each row has job info
    rate: '', // Rate per hour from CEC
    amount: '' // Total amount (hours * rate) from CEC
  });

  // Split combined job name/number field
  const splitJobNameNumber = (combinedValue: string): { jobNumber: string; jobName: string } => {
    if (!combinedValue || typeof combinedValue !== 'string') {
      return { jobNumber: '', jobName: '' };
    }

    // Pattern 1: ##-##### format (like 23-25020)
    const pattern1 = /^(\d{2}-\d{5})\s+(.+)$/;
    const match1 = combinedValue.match(pattern1);
    if (match1) {
      return {
        jobNumber: match1[1].trim(),
        jobName: match1[2].trim()
      };
    }

    // Pattern 2: CI##-## format (like CI23-52)
    const pattern2 = /^(CI\d{2}-\d{2})\s+(.+)$/;
    const match2 = combinedValue.match(pattern2);
    if (match2) {
      return {
        jobNumber: match2[1].trim(),
        jobName: match2[2].trim()
      };
    }

    // Pattern 3: More flexible - any job code followed by space and description
    const pattern3 = /^([A-Z0-9-]{4,12})\s+(.+)$/;
    const match3 = combinedValue.match(pattern3);
    if (match3) {
      return {
        jobNumber: match3[1].trim(),
        jobName: match3[2].trim()
      };
    }

    // If no pattern matches, return the original as job name
    return {
      jobNumber: '',
      jobName: combinedValue.trim()
    };
  };

  // Smart column detection function
  const applySmartColumnDetection = (headers: string[]) => {
    const mappings: any = {};
    
    const smartColumnMappings = {
      firstName: {
        exact: ['first name', 'firstname', 'fname', 'first'],
        contains: ['first'],
        patterns: [/^first/i, /fname/i]
      },
      lastName: {
        exact: ['last name', 'lastname', 'lname', 'last', 'surname'],
        contains: ['last', 'surname'],
        patterns: [/^last/i, /lname/i, /surname/i]
      },
      employeeName: {
        exact: ['employee name', 'employeename', 'name', 'employee', 'full name', 'fullname'],
        contains: ['employee', 'name'],
        patterns: [/employee.*name/i, /full.*name/i, /^name$/i]
      },
      employeeCode: {
        exact: ['employee code', 'employeecode', 'emp code', 'empcode', 'employee id', 'employeeid', 'emp id', 'empid', 'id'],
        contains: ['employee code', 'emp code', 'employee id', 'emp id'],
        patterns: [/employee.*code/i, /emp.*code/i, /employee.*id/i, /emp.*id/i, /^id$/i]
      },
      date: {
        exact: ['date', 'work date', 'workdate', 'day', 'work day', 'workday'],
        contains: ['date', 'day'],
        patterns: [/date/i, /day/i]
      },
      hours: {
        exact: ['hours', 'total hours', 'totalhours', 'hours worked', 'hoursworked', 'time', 'hrs'],
        contains: ['hours', 'hrs', 'time'],
        patterns: [/hours/i, /hrs/i, /time/i]
      },
      payType: {
        exact: ['pay type', 'paytype', 'type', 'overtime', 'ot', 'regular', 'reg'],
        contains: ['pay type', 'paytype', 'overtime', 'regular'],
        patterns: [/pay.*type/i, /overtime/i, /regular/i, /^ot$/i, /^reg$/i]
      },
      activityCode: {
        exact: ['activity code', 'activitycode', 'code', 'dist activity code', 'distribution activity code'],
        contains: ['activity', 'dist activity', 'cost code', 'code'],
        patterns: [/activity.*code/i, /dist.*activity/i, /cost.*code/i]
      },
      activityDescription: {
        exact: ['activity description', 'activitydescription', 'description', 'desc', 'activity desc'],
        contains: ['description', 'desc', 'activity'],
        patterns: [/activity.*desc/i, /description/i, /^desc$/i]
      },
      jobName: {
        exact: ['job name', 'jobname', 'job', 'project', 'project name', 'projectname'],
        contains: ['job', 'project'],
        patterns: [/job.*name/i, /project/i, /^job$/i]
      },
      jobNumber: {
        exact: ['job number', 'jobnumber', 'job #', 'job no', 'jobno', 'project number', 'projectnumber'],
        contains: ['job number', 'job #', 'job no', 'project number'],
        patterns: [/job.*number/i, /job.*#/i, /job.*no/i, /project.*number/i]
      },
      rate: {
        exact: ['rate', 'hourly rate', 'hourlyrate', 'pay rate', 'payrate', 'wage rate', 'wagerate'],
        contains: ['rate', 'wage'],
        patterns: [/rate/i, /wage/i, /pay.*rate/i, /hourly.*rate/i]
      },
      amount: {
        exact: ['amount', 'total amount', 'totalamount', 'pay amount', 'payamount', 'total pay', 'totalpay', 'gross pay', 'grosspay'],
        contains: ['amount', 'total', 'pay'],
        patterns: [/amount/i, /total.*pay/i, /gross.*pay/i, /pay.*amount/i]
      }
    };

    // Score and map each header to the best field
    headers.forEach(header => {
      const headerLower = header.toLowerCase().trim();
      let bestMatch = { field: '', score: 0 };

      Object.entries(smartColumnMappings).forEach(([field, criteria]) => {
        let score = 0;

        // Check exact matches (highest score)
        if (criteria.exact.some(exact => exact === headerLower)) {
          score = 100;
        }
        // Check contains matches
        else if (criteria.contains.some(contains => headerLower.includes(contains))) {
          score = 80;
        }
        // Check pattern matches
        else if (criteria.patterns.some(pattern => pattern.test(header))) {
          score = 60;
        }

        if (score > bestMatch.score) {
          bestMatch = { field, score };
        }
      });

      // Only auto-map if we have a strong confidence (score >= 60)
      if (bestMatch.score >= 60) {
        mappings[bestMatch.field] = header;
      }
    });

    return mappings;
  };

  function getDefaultWeekEnding(): string {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
    const nextSunday = new Date(today);
    nextSunday.setDate(today.getDate() + daysUntilSunday);
    return nextSunday.toISOString().split('T')[0];
  }

  // Function to validate and adjust week ending date to ensure it's a Sunday
  function validateWeekEndingDate(dateString: string): string {
    console.log('validateWeekEndingDate input:', dateString);
    
    // Parse date components manually to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(num => parseInt(num));
    const date = new Date(year, month - 1, day); // months are 0-indexed, create at local midnight
    
    console.log('Created date object:', date);
    const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
    console.log('Day of week (0=Sun):', dayOfWeek);
    
    // If it's already Sunday, keep it as is
    if (dayOfWeek === 0) {
      console.log('Already Sunday, keeping as:', dateString);
      return dateString; // Return original string to avoid timezone conversion
    }
    
    // For other days, find the next Sunday to end the week
    const daysUntilNextSunday = 7 - dayOfWeek;
    date.setDate(date.getDate() + daysUntilNextSunday);
    
    // Format back to YYYY-MM-DD string manually to avoid timezone issues
    const adjustedYear = date.getFullYear();
    const adjustedMonth = String(date.getMonth() + 1).padStart(2, '0'); // months are 0-indexed
    const adjustedDay = String(date.getDate()).padStart(2, '0');
    const result = `${adjustedYear}-${adjustedMonth}-${adjustedDay}`;
    
    console.log('Adjusted to next Sunday:', result);
    
    return result;
  }

  useEffect(() => {
    loadEmployees();
    loadEmployeeNameMappings();
    fetchContractors();
  }, []);

  const fetchContractors = async () => {
    try {
      console.log('Fetching contractors from TimesheetImport...');
      const contractorsRef = collection(db, 'contractors');
      const snapshot = await getDocs(contractorsRef);
      
      console.log('TimesheetImport - Raw snapshot docs:', snapshot.docs.length);
      
      const contractorData = snapshot.docs.map(doc => {
        const data = doc.data();
        console.log(`TimesheetImport - Doc ${doc.id}:`, data);
        return {
          id: doc.id,
          name: data.companyName || data.name || 'Unknown', // Try both field names
          markupPercentage: data.markupPercentage || 4.00,
          ...data
        };
      });
      
      console.log('TimesheetImport - Processed contractors:', contractorData);
      setContractors(contractorData);
      
      // Set default contractor if none selected
      if (contractorData.length > 0 && !selectedContractor) {
        const defaultContractor = contractorData.find(c => c.name?.toLowerCase().includes('cec')) || contractorData[0];
        console.log('TimesheetImport - Setting default contractor:', defaultContractor);
        setSelectedContractor({
          id: defaultContractor.id,
          name: defaultContractor.name,
          markupPercentage: defaultContractor.markupPercentage || 4.00
        });
      } else if (contractorData.length === 0) {
        console.log('TimesheetImport - No contractors found in database');
        // Use fallback contractors
        const fallbackContractors = [
          { id: 'cec', name: 'CEC', markupPercentage: 4.00 },
          { id: 'reliable', name: 'Reliable', markupPercentage: 4.50 },
          { id: 'btc', name: 'BTC', markupPercentage: 25.00 }
        ];
        console.log('TimesheetImport - Using fallback contractors:', fallbackContractors);
        setContractors(fallbackContractors);
        setSelectedContractor(fallbackContractors[0]);
      }
    } catch (err) {
      console.error('TimesheetImport - Error fetching contractors:', err);
      // Use fallback contractors
      const fallbackContractors = [
        { id: 'cec', name: 'CEC', markupPercentage: 4.00 },
        { id: 'reliable', name: 'Reliable', markupPercentage: 4.50 },
        { id: 'btc', name: 'BTC', markupPercentage: 25.00 }
      ];
      console.log('TimesheetImport - Using fallback contractors due to error:', fallbackContractors);
      setContractors(fallbackContractors);
      setSelectedContractor(fallbackContractors[0]);
    }
  };

  // Load saved employee name mappings from localStorage
  const loadEmployeeNameMappings = () => {
    try {
      const saved = localStorage.getItem('employeeNameMappings');
      if (saved) {
        setEmployeeNameMappings(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Error loading employee name mappings:', error);
    }
  };

  // Save employee name mappings to localStorage
  const saveEmployeeNameMappings = (mappings: {[key: string]: string}) => {
    try {
      localStorage.setItem('employeeNameMappings', JSON.stringify(mappings));
      setEmployeeNameMappings(mappings);
    } catch (error) {
      console.error('Error saving employee name mappings:', error);
    }
  };

  const loadEmployees = async () => {
    console.log('[EMPLOYEE DEBUG] loadEmployees called');
    try {
      const employeesRef = collection(db, 'employees');
      
      // For timesheet imports, we need both active AND inactive employees
      // so that previously created inactive employees show up for mapping
      let snapshot;
      try {
        // Use the same query as Employee Management - order by 'name' field
        const q = query(employeesRef, orderBy('name'));
        snapshot = await getDocs(q);
        console.log(`[EMPLOYEE DEBUG] Query with orderBy('name') successful, got ${snapshot.docs.length} docs`);
      } catch (indexError) {
        console.warn('OrderBy index not available, trying orderBy firstName:', indexError);
        try {
          // Try orderBy firstName as fallback
          const q2 = query(employeesRef, orderBy('firstName'));
          snapshot = await getDocs(q2);
          console.log(`[EMPLOYEE DEBUG] Query with orderBy('firstName') successful, got ${snapshot.docs.length} docs`);
        } catch (indexError2) {
          console.warn('OrderBy firstName also failed, falling back to simple query:', indexError2);
          // Final fallback to simple query without orderBy
          snapshot = await getDocs(employeesRef);
          console.log(`[EMPLOYEE DEBUG] Simple query successful, got ${snapshot.docs.length} docs`);
        }
      }
      
      const employeeData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          hireDate: data.hireDate?.toDate(),
          terminationDate: data.terminationDate?.toDate(),
          licenseExpiration: data.licenseExpiration?.toDate(),
        };
      }) as Employee[];

      // Look specifically for Pablo Aguilar
      const pabloMatches = employeeData.filter(emp => {
        const firstName = (emp.firstName || '').toLowerCase();
        const lastName = (emp.lastName || '').toLowerCase();
        const name = (emp.name || '').toLowerCase();
        const fullName = `${firstName} ${lastName}`.trim();
        
        return firstName.includes('pablo') || lastName.includes('aguilar') || 
               name.includes('pablo') || name.includes('aguilar') || 
               fullName.includes('pablo aguilar');
      });
      
      console.log(`[PABLO DEBUG] Loaded ${employeeData.length} employees, found ${pabloMatches.length} Pablo/Aguilar matches:`, 
                  pabloMatches.map(emp => ({ id: emp.id, firstName: emp.firstName, lastName: emp.lastName, name: emp.name, status: emp.status })));
      
      if (pabloMatches.length === 0) {
        console.log(`[PABLO DEBUG] Pablo not found! First 5 loaded employees:`);
        employeeData.slice(0, 5).forEach((emp, index) => {
          console.log(`  ${index + 1}. firstName: "${emp.firstName}", lastName: "${emp.lastName}", name: "${emp.name}", status: "${emp.status}"`);
        });
        console.log(`[PABLO DEBUG] Total employees loaded: ${employeeData.length}, Employee Management shows 56. Missing ${56 - employeeData.length} employees.`);
      }

      // Sort manually (active first, then inactive, then by name)
      employeeData.sort((a, b) => {
        // Active employees first
        if (a.status === 'active' && b.status !== 'active') return -1;
        if (b.status === 'active' && a.status !== 'active') return 1;
        
        // Then sort by name
        const aName = a.firstName || a.name || '';
        const bName = b.firstName || b.name || '';
        return aName.localeCompare(bName);
      });

      setEmployees(employeeData);
    } catch (err) {
      console.error('Error loading employees:', err);
      setError(`Failed to load employees: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const extractHeaders = useCallback((file: File) => {
    setIsLoading(true);
    setError(null);
    setExcelFile(file);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
        const hasHeaders = range.e.r > 0;
        
        let headers: string[] = [];
        let examples: any = {};
        
        if (hasHeaders) {
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: false });
          
          if (jsonData.length > 0) {
            headers = Object.keys(jsonData[0] as Record<string, any>);
            headers.forEach(header => {
              examples[header] = (jsonData[0] as any)[header];
            });
          }
        }
        
        if (headers.length === 0) {
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
            raw: false,
            header: 'A'
          });
          
          if (jsonData.length > 0) {
            headers = Object.keys(jsonData[0] as Record<string, any>);
            headers.forEach(header => {
              examples[header] = (jsonData[0] as any)[header];
            });
          } else {
            setError('No data found in Excel file');
            setIsLoading(false);
            return;
          }
        }
        
        setExcelHeaders(headers);
        setColumnExamples(examples);
        
        // Apply smart column detection
        const smartMappings = applySmartColumnDetection(headers);
        setColumnMapping(prev => ({ ...prev, ...smartMappings }));
        
        setShowMapping(true);
        setIsLoading(false);
      } catch (err) {
        console.error('Error reading file:', err);
        setError(`Error reading file: ${err}`);
        setIsLoading(false);
      }
    };
    
    reader.onerror = () => {
      setError('Error reading file');
      setIsLoading(false);
    };
    
    reader.readAsArrayBuffer(file);
  }, []);

  const processExcelFile = useCallback(() => {
    if (!excelFile) return;
    
    setIsLoading(true);
    setError(null);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        const hasLetterHeaders = Object.values(columnMapping).some(value => 
          value && /^[A-Z]$/.test(value)
        );
        
        let jsonData: any[];
        if (hasLetterHeaders) {
          jsonData = XLSX.utils.sheet_to_json(worksheet, { 
            raw: false,
            header: 'A'
          });
        } else {
          jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: false });
        }

        // Group data by job if creating separate timesheets
        const groupedData: { [key: string]: TimesheetImportData } = {};
        
        // Group by employee first, then by job
        const employeeHours: { [key: string]: { [jobKey: string]: any } } = {};
        
        for (const row of jsonData) {
          let jobName = importSettings.jobName;
          let jobNumber = importSettings.jobNumber;
          
          // Override with row-level job info if mapped
          if (columnMapping.jobName && row[columnMapping.jobName]) {
            let rawJobName = row[columnMapping.jobName];
            
            // Auto-split job name/number if enabled and jobNumber is not already mapped
            if (autoSplitJobName && rawJobName && (!columnMapping.jobNumber || !row[columnMapping.jobNumber])) {
              const { jobNumber: extractedNumber, jobName: extractedName } = splitJobNameNumber(rawJobName);
              if (extractedNumber) {
                jobNumber = extractedNumber;
                jobName = extractedName;
              } else {
                jobName = rawJobName;
              }
            } else {
              jobName = rawJobName;
            }
          }
          if (columnMapping.jobNumber && row[columnMapping.jobNumber]) {
            jobNumber = row[columnMapping.jobNumber];
          }
          
          const jobKey = importSettings.createSeparateTimesheets 
            ? `${jobName}-${jobNumber}` 
            : 'single';
          
          // Get employee name
          let employeeName = '';
          if (columnMapping.employeeName && row[columnMapping.employeeName]) {
            employeeName = row[columnMapping.employeeName];
          } else if (columnMapping.employeeCode && row[columnMapping.employeeCode]) {
            employeeName = row[columnMapping.employeeCode];
          } else if (columnMapping.firstName && columnMapping.lastName) {
            const firstName = row[columnMapping.firstName] || '';
            const lastName = row[columnMapping.lastName] || '';
            employeeName = `${firstName} ${lastName}`.trim();
          } else if (columnMapping.firstName) {
            employeeName = row[columnMapping.firstName] || '';
          }
          
          if (!employeeName) continue; // Skip rows without employee names
          
          // Get date and determine day of week
          const dateStr = row[columnMapping.date] || '';
          if (!dateStr) continue; // Skip rows without date
          
          const date = new Date(dateStr);
          if (isNaN(date.getTime())) continue; // Skip invalid dates
          
          const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
          const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
          const dayName = dayNames[dayOfWeek];
          
          // Get hours and pay type for this day
          const hours = parseFloat(row[columnMapping.hours] || 0);
          if (hours <= 0) continue; // Skip rows with no hours
          
          const payType = row[columnMapping.payType] || '';
          const isOvertime = payType.toLowerCase().includes('overtime') || payType.toLowerCase().includes('ot');
          
          // Get rate information from CEC timesheet
          const rawRate = row[columnMapping.rate] || '';
          const rawAmount = row[columnMapping.amount] || '';
          
          // Clean currency strings (remove $, commas, spaces) and parse
          const cecRate = parseFloat(rawRate.toString().replace(/[$,\s]/g, '')) || 0;
          const cecAmount = parseFloat(rawAmount.toString().replace(/[$,\s]/g, '')) || 0;
          
          // Debug rate extraction
          if (columnMapping.rate || columnMapping.amount) {
            console.log(`RATE COMPARISON: Rate extraction for ${employeeName}:`, {
              rateMapped: columnMapping.rate,
              amountMapped: columnMapping.amount,
              rateValue: row[columnMapping.rate],
              amountValue: row[columnMapping.amount],
              cecRate,
              cecAmount,
              payType
            });
          } else {
            // Only log this once per processing session
            if (!window.rateComparisonNoMappingLogged) {
              console.log(`RATE COMPARISON: ⚠️ NO RATE/AMOUNT COLUMNS MAPPED - Rate comparison will not work without mapping these fields!`);
              console.log(`RATE COMPARISON: Available column mappings:`, columnMapping);
              window.rateComparisonNoMappingLogged = true;
            }
          }
          
          // Create employee key
          const empKey = `${jobKey}|${employeeName}`;
          
          if (!employeeHours[empKey]) {
            employeeHours[empKey] = {
              jobName,
              jobNumber,
              employeeName,
              firstName: row[columnMapping.firstName] || (employeeName ? employeeName.split(' ')[0] : '') || '',
              lastName: row[columnMapping.lastName] || (employeeName ? employeeName.split(' ').slice(1).join(' ') : '') || '',
              monday: 0,
              tuesday: 0,
              wednesday: 0,
              thursday: 0,
              friday: 0,
              saturday: 0,
              sunday: 0,
              // Add Regular/OT tracking
              mondayRegular: 0,
              tuesdayRegular: 0,
              wednesdayRegular: 0,
              thursdayRegular: 0,
              fridayRegular: 0,
              saturdayRegular: 0,
              sundayRegular: 0,
              mondayOT: 0,
              tuesdayOT: 0,
              wednesdayOT: 0,
              thursdayOT: 0,
              fridayOT: 0,
              saturdayOT: 0,
              sundayOT: 0,
              activityCode: row[columnMapping.activityCode] || '',
              activityDescription: row[columnMapping.activityDescription] || '',
              // CEC Rate tracking - store first rate encountered for this employee
              cecRate: cecRate || 0,
              cecAmount: (cecAmount || 0),
              cecEarningDesc: payType,
              // Track rate entries for potential discrepancy analysis
              rateEntries: []
            };
          }
          
          // Add rate entry for this row
          if (cecRate > 0) {
            employeeHours[empKey].rateEntries.push({
              date: dateStr,
              day: dayName,
              hours,
              rate: cecRate,
              amount: cecAmount,
              payType: payType,
              isOvertime
            });
          }
          
          // Add hours to the appropriate day and type
          employeeHours[empKey][dayName] += hours;
          if (isOvertime) {
            employeeHours[empKey][dayName + 'OT'] += hours;
          } else {
            employeeHours[empKey][dayName + 'Regular'] += hours;
          }
        }
        
        // Convert to grouped data format
        for (const [empKey, empData] of Object.entries(employeeHours)) {
          const jobKey = importSettings.createSeparateTimesheets 
            ? `${empData.jobName}-${empData.jobNumber}` 
            : 'single';
          
          if (!groupedData[jobKey]) {
            const validatedDateString = validateWeekEndingDate(importSettings.weekEnding);
            // Parse the validated date string manually to avoid timezone issues
            const [year, month, day] = validatedDateString.split('-').map(num => parseInt(num));
            const weekEndingDate = new Date(year, month - 1, day); // months are 0-indexed
            
            groupedData[jobKey] = {
              jobName: empData.jobName,
              jobNumber: empData.jobNumber,
              weekEnding: weekEndingDate,
              employeeData: []
            };
          }
          
          groupedData[jobKey].employeeData.push({
            firstName: empData.firstName,
            lastName: empData.lastName,
            monday: empData.monday,
            tuesday: empData.tuesday,
            wednesday: empData.wednesday,
            thursday: empData.thursday,
            friday: empData.friday,
            saturday: empData.saturday,
            sunday: empData.sunday,
            // Regular/OT breakdown
            mondayRegular: empData.mondayRegular,
            tuesdayRegular: empData.tuesdayRegular,
            wednesdayRegular: empData.wednesdayRegular,
            thursdayRegular: empData.thursdayRegular,
            fridayRegular: empData.fridayRegular,
            saturdayRegular: empData.saturdayRegular,
            sundayRegular: empData.sundayRegular,
            mondayOT: empData.mondayOT,
            tuesdayOT: empData.tuesdayOT,
            wednesdayOT: empData.wednesdayOT,
            thursdayOT: empData.thursdayOT,
            fridayOT: empData.fridayOT,
            saturdayOT: empData.saturdayOT,
            sundayOT: empData.sundayOT,
            activityCode: empData.activityCode,
            activityDescription: empData.activityDescription,
            // CEC Rate information
            cecRate: empData.cecRate,
            cecAmount: empData.cecAmount,
            cecEarningDesc: empData.cecEarningDesc
          });
        }
        
        // Check for unmatched employees
        const unmatched: string[] = [];
        const unmatchedWithRates: string[] = []; // Track employees with rate data that aren't found
        
        try {
          Object.values(groupedData).forEach(timesheet => {
            timesheet.employeeData.forEach(emp => {
              try {
                const dbEmployee = matchEmployeeToDatabase(emp.firstName || '', emp.lastName || '');
                const fullName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || 'Unknown Employee';
                
                if (!dbEmployee) {
                  if (!unmatched.includes(fullName)) {
                    unmatched.push(fullName);
                  }
                  // Track if this unmatched employee has rate data
                  if (emp.cecRate && emp.cecRate > 0 && !unmatchedWithRates.includes(fullName)) {
                    unmatchedWithRates.push(fullName);
                  }
                }
              } catch (empErr) {
                console.error('Error processing employee:', emp, empErr);
                // Continue processing other employees
              }
            });
          });
          
          // Add additional logging for rate analysis issues
          if (unmatchedWithRates.length > 0) {
            console.log('RATE COMPARISON: Employees with rate data that are not found in database:', unmatchedWithRates);
          }
          
        } catch (unmatchedErr) {
          console.error('Error checking unmatched employees:', unmatchedErr);
        }
        
        setUnmatchedEmployees(unmatched);
        setImportedData(Object.values(groupedData));
        setShowMapping(false);
        
        // Debug: Check column mappings
        console.log('RATE COMPARISON: Column mappings:', columnMapping);
        
        // Debug: Check if rate data was captured
        console.log('RATE COMPARISON: Checking for rate data in grouped data...');
        Object.values(groupedData).forEach((timesheet, index) => {
          console.log(`RATE COMPARISON: Timesheet ${index}:`, timesheet.jobName);
          timesheet.employeeData.forEach((emp, empIndex) => {
            if (emp.cecRate && emp.cecRate > 0) {
              console.log(`RATE COMPARISON:   Employee ${empIndex} (${emp.firstName} ${emp.lastName}): Rate $${emp.cecRate}, Amount $${emp.cecAmount}, Type: ${emp.cecEarningDesc}`);
            } else {
              console.log(`RATE COMPARISON:   Employee ${empIndex} (${emp.firstName} ${emp.lastName}): No rate data (cecRate: ${emp.cecRate})`);
            }
          });
        });
        
        // Perform rate analysis if we have rate information and a selected contractor
        const hasRateData = Object.values(groupedData).some(timesheet => 
          timesheet.employeeData.some(emp => emp.cecRate && emp.cecRate > 0)
        );
        
        console.log('RATE COMPARISON: hasRateData:', hasRateData, 'selectedContractor:', selectedContractor?.name, 'currentUser:', !!currentUser);
        
        if (hasRateData && selectedContractor && currentUser) {
          console.log('RATE COMPARISON: Performing rate analysis...');
          try {
            // Find the full contractor object
            console.log('RATE COMPARISON: Looking for contractor with id:', selectedContractor.id);
            console.log('RATE COMPARISON: Available contractors:', contractors.map(c => ({id: c.id, name: c.companyName})));
            const fullContractor = contractors.find(c => c.id === selectedContractor.id);
            if (!fullContractor) {
              console.error('RATE COMPARISON: Could not find full contractor object for:', selectedContractor.name);
              console.error('RATE COMPARISON: Selected contractor:', selectedContractor);
              setError('Rate analysis failed: contractor configuration not found');
              return;
            }
            
            console.log('RATE COMPARISON: Full contractor object:', fullContractor);
            
            // Analyze rates across all jobs/timesheets
            const completeAnalysis = analyzeCompleteTimesheetRates(
              Object.values(groupedData),
              employees,
              fullContractor,
              currentUser.uid,
              employeeNameMappings
            );
            
            setRateAnalysis(completeAnalysis);
            setShowRateAnalysis(true);
            console.log('RATE COMPARISON: Complete rate analysis finished:', completeAnalysis);
          } catch (analysisError) {
            console.error('RATE COMPARISON: Error performing rate analysis:', analysisError);
            setError('Rate analysis failed, but timesheet data was processed successfully');
          }
        }
        
        // Show employee mapping if there are unmatched employees
        if (unmatched.length > 0) {
          setShowEmployeeMapping(true);
        }
        
        setIsLoading(false);
      } catch (err) {
        console.error('Error processing file:', err);
        setError(`Error processing file: ${err}`);
        setIsLoading(false);
      }
    };
    
    reader.readAsArrayBuffer(excelFile);
  }, [excelFile, columnMapping, importSettings]);

  const matchEmployeeToDatabase = (firstName: string, lastName: string): Employee | null => {
    // Clean and normalize the input names
    const cleanFirstName = (firstName || '').trim().replace(/\s+/g, ' ');
    const cleanLastName = (lastName || '').trim().replace(/\s+/g, ' ');
    const fullName = `${cleanFirstName} ${cleanLastName}`.trim().toLowerCase();
    
    // Only log for Pablo Aguilar to reduce noise
    if (fullName.includes('pablo')) {
      console.log(`[PABLO MATCH DEBUG] Looking for: "${firstName}" + "${lastName}" = "${fullName}"`);
      console.log(`[PABLO MATCH DEBUG] Available employees: ${employees.length}`);
      
      const pabloSearch = employees.filter(emp => {
        const empFullName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim().toLowerCase();
        const empName = (emp.name || '').toLowerCase();
        return empFullName.includes('pablo') || empFullName.includes('aguilar') || empName.includes('pablo') || empName.includes('aguilar');
      });
      
      console.log(`[PABLO MATCH DEBUG] Pablo/Aguilar matches in database:`, 
                  pabloSearch.map(emp => ({ firstName: emp.firstName, lastName: emp.lastName, name: emp.name })));
    }
    
    if (!fullName) return null; // Return null if no name provided
    
    // Check if there's a saved mapping for this name
    if (employeeNameMappings[fullName]) {
      const mappedId = employeeNameMappings[fullName];
      const mappedEmployee = employees.find(emp => emp.id === mappedId);
      if (mappedEmployee) {
        return mappedEmployee;
      }
    }
    
    // Try exact full name match first (case-insensitive)
    let match = employees.find(emp => {
      const empFirstName = (emp.firstName || '').trim().replace(/\s+/g, ' ');
      const empLastName = (emp.lastName || '').trim().replace(/\s+/g, ' ');
      const empFullName = `${empFirstName} ${empLastName}`.trim().toLowerCase();
      const empNameField = (emp.name || '').trim().replace(/\s+/g, ' ').toLowerCase();
      
      const fullNameMatch = empFullName === fullName;
      const nameFieldMatch = empNameField === fullName;
      
      if (fullNameMatch || nameFieldMatch) {
        console.log(`[MATCH DEBUG] Full name match found: "${empFullName}" or "${empNameField}" = "${fullName}"`);
      }
      
      return fullNameMatch || nameFieldMatch;
    });
    
    // Try first name + last name parts match if exact doesn't work
    if (!match && cleanFirstName && cleanLastName) {
      match = employees.find(emp => {
        const empFirstName = (emp.firstName || '').trim().replace(/\s+/g, ' ').toLowerCase();
        const empLastName = (emp.lastName || '').trim().replace(/\s+/g, ' ').toLowerCase();
        
        const firstMatch = empFirstName === cleanFirstName.toLowerCase();
        const lastMatch = empLastName === cleanLastName.toLowerCase();
        
        if (firstMatch && lastMatch) {
          console.log(`[MATCH DEBUG] Parts match found: "${empFirstName}" + "${empLastName}" = "${cleanFirstName.toLowerCase()}" + "${cleanLastName.toLowerCase()}"`);
        }
        
        return firstMatch && lastMatch;
      });
    }
    
    // Try first name match if exact doesn't work
    if (!match && cleanFirstName) {
      match = employees.find(emp => {
        const empFirstName = (emp.firstName || emp.name || '').trim().replace(/\s+/g, ' ');
        const firstMatch = empFirstName && empFirstName.toLowerCase() === cleanFirstName.toLowerCase();
        
        if (firstMatch) {
          console.log(`[MATCH DEBUG] First name match found: "${empFirstName}" = "${cleanFirstName}"`);
        }
        
        return firstMatch;
      });
    }
    
    // Only log results for Pablo to reduce noise
    if (fullName.includes('pablo')) {
      if (!match) {
        console.log(`[PABLO MATCH DEBUG] No match found for "${fullName}"`);
      } else {
        console.log(`[PABLO MATCH DEBUG] Final match: ${match.firstName} ${match.lastName || match.name}`);
      }
    }
    
    return match || null;
  };

  // Function to find existing timesheet for same week/job (same as in TimesheetEntry)
  const findExistingTimesheet = async (jobName: string, jobNumber: string, weekEnding: Date): Promise<WeeklyTimesheet | null> => {
    try {
      const timesheetsRef = collection(db, 'weeklyTimesheets');
      
      // Create query to find timesheets with same job name and week ending
      // We'll compare dates by converting to string format to avoid timezone issues
      const weekEndingStr = weekEnding.toISOString().split('T')[0];
      
      const q = query(timesheetsRef, where('jobName', '==', jobName));
      const snapshot = await getDocs(q);
      
      // Filter results to match job number and week ending date
      const matchingTimesheets = snapshot.docs.filter(doc => {
        const data = doc.data();
        const docWeekEnding = data.weekEnding?.toDate();
        const docWeekEndingStr = docWeekEnding?.toISOString().split('T')[0];
        
        // Match job number (both empty/undefined should match)
        const jobNumberMatch = (data.jobNumber || '') === (jobNumber || '');
        
        // Match week ending date
        const dateMatch = docWeekEndingStr === weekEndingStr;
        
        return jobNumberMatch && dateMatch;
      });
      
      if (matchingTimesheets.length > 0) {
        const timesheetDoc = matchingTimesheets[0];
        return {
          id: timesheetDoc.id,
          ...timesheetDoc.data(),
          weekEnding: timesheetDoc.data().weekEnding?.toDate() || new Date(),
          createdAt: timesheetDoc.data().createdAt?.toDate() || new Date(),
          updatedAt: timesheetDoc.data().updatedAt?.toDate() || new Date()
        } as WeeklyTimesheet;
      }
      
      return null;
    } catch (err) {
      console.error('Error finding existing timesheet:', err);
      return null;
    }
  };

  // Function to combine employee entries, avoiding duplicates (same as in TimesheetEntry)
  const combineTimesheetEntries = (existingEntries: WeeklyTimesheetEntry[], newEntries: WeeklyTimesheetEntry[]): WeeklyTimesheetEntry[] => {
    const combined = [...existingEntries];
    
    for (const newEntry of newEntries) {
      // Check if employee already exists in the timesheet
      const existingIndex = combined.findIndex(entry => entry.employeeId === newEntry.employeeId);
      
      if (existingIndex >= 0) {
        // Employee exists, combine their hours
        const existing = combined[existingIndex];
        const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
        
        // Combine regular hours
        const combinedHours: WeeklyTimesheetEntry['hours'] = { ...existing.hours };
        const combinedRegularHours: WeeklyTimesheetEntry['regularHours'] = { ...existing.regularHours };
        const combinedOvertimeHours: WeeklyTimesheetEntry['overtimeHours'] = { ...existing.overtimeHours };
        const combinedActivities: WeeklyTimesheetEntry['activities'] = { ...existing.activities };
        
        for (const day of days) {
          // Add hours
          combinedHours[day] = (combinedHours[day] || 0) + (newEntry.hours[day] || 0);
          
          // Add regular/overtime hours if available
          if (newEntry.regularHours) {
            combinedRegularHours[day] = (combinedRegularHours[day] || 0) + (newEntry.regularHours[day] || 0);
          }
          if (newEntry.overtimeHours) {
            combinedOvertimeHours[day] = (combinedOvertimeHours[day] || 0) + (newEntry.overtimeHours[day] || 0);
          }
          
          // Combine activities
          const existingDayActivities = existing.activities?.[day] || [];
          const newDayActivities = newEntry.activities?.[day] || [];
          
          if (newDayActivities.length > 0) {
            combinedActivities[day] = [...existingDayActivities, ...newDayActivities];
          }
        }
        
        combined[existingIndex] = {
          ...existing,
          hours: combinedHours,
          regularHours: combinedRegularHours,
          overtimeHours: combinedOvertimeHours,
          activities: combinedActivities,
          // Update activity code/description if new entry has them
          activityCode: newEntry.activityCode || existing.activityCode,
          activityDescription: newEntry.activityDescription || existing.activityDescription
        };
      } else {
        // New employee, add to list
        combined.push(newEntry);
      }
    }
    
    return combined;
  };

  const saveTimesheets = async () => {
    console.log('saveTimesheets function called');
    console.log('importedData:', importedData);
    console.log('currentUser:', currentUser);
    console.log('selectedContractor:', selectedContractor);
    
    if (!importedData || !currentUser) {
      console.log('Missing required data - importedData or currentUser');
      return;
    }
    
    // Validate contractor selection
    if (!selectedContractor) {
      console.log('No contractor selected');
      setError('Please select a contractor before saving timesheets');
      return;
    }
    
    try {
      console.log('Starting save process...');
      setIsLoading(true);
      const savedTimesheets: string[] = [];
      const combinedTimesheets: string[] = [];
      
      for (const timesheetData of importedData) {
        const entries: WeeklyTimesheetEntry[] = [];
        
        // Group employee data by name (in case of multiple activity entries)
        const employeeGroups: { [key: string]: any[] } = {};
        timesheetData.employeeData.forEach(empData => {
          const key = `${empData.firstName} ${empData.lastName}`;
          if (!employeeGroups[key]) {
            employeeGroups[key] = [];
          }
          employeeGroups[key].push(empData);
        });
        
        // Create entries for each employee
        for (const [employeeName, empDataArray] of Object.entries(employeeGroups)) {
          const firstName = empDataArray[0].firstName;
          const lastName = empDataArray[0].lastName;
          const dbEmployee = matchEmployeeToDatabase(firstName, lastName);
          
          // Sum up hours if multiple entries per employee
          const totalHours = {
            monday: empDataArray.reduce((sum, emp) => sum + (emp.monday || 0), 0),
            tuesday: empDataArray.reduce((sum, emp) => sum + (emp.tuesday || 0), 0),
            wednesday: empDataArray.reduce((sum, emp) => sum + (emp.wednesday || 0), 0),
            thursday: empDataArray.reduce((sum, emp) => sum + (emp.thursday || 0), 0),
            friday: empDataArray.reduce((sum, emp) => sum + (emp.friday || 0), 0),
            saturday: empDataArray.reduce((sum, emp) => sum + (emp.saturday || 0), 0),
            sunday: empDataArray.reduce((sum, emp) => sum + (emp.sunday || 0), 0)
          };
          
          // Debug: Check what's in empDataArray
          console.log(`Employee ${employeeName} empDataArray:`, empDataArray);
          console.log(`First emp object:`, empDataArray[0]);
          
          // Calculate Regular/OT hours for each day
          const regularHours = {
            monday: empDataArray.reduce((sum, emp) => sum + (emp.mondayRegular || 0), 0),
            tuesday: empDataArray.reduce((sum, emp) => sum + (emp.tuesdayRegular || 0), 0),
            wednesday: empDataArray.reduce((sum, emp) => sum + (emp.wednesdayRegular || 0), 0),
            thursday: empDataArray.reduce((sum, emp) => sum + (emp.thursdayRegular || 0), 0),
            friday: empDataArray.reduce((sum, emp) => sum + (emp.fridayRegular || 0), 0),
            saturday: empDataArray.reduce((sum, emp) => sum + (emp.saturdayRegular || 0), 0),
            sunday: empDataArray.reduce((sum, emp) => sum + (emp.sundayRegular || 0), 0)
          };
          
          console.log(`Calculated regularHours for ${employeeName}:`, regularHours);

          const overtimeHours = {
            monday: empDataArray.reduce((sum, emp) => sum + (emp.mondayOT || 0), 0),
            tuesday: empDataArray.reduce((sum, emp) => sum + (emp.tuesdayOT || 0), 0),
            wednesday: empDataArray.reduce((sum, emp) => sum + (emp.wednesdayOT || 0), 0),
            thursday: empDataArray.reduce((sum, emp) => sum + (emp.thursdayOT || 0), 0),
            friday: empDataArray.reduce((sum, emp) => sum + (emp.fridayOT || 0), 0),
            saturday: empDataArray.reduce((sum, emp) => sum + (emp.saturdayOT || 0), 0),
            sunday: empDataArray.reduce((sum, emp) => sum + (emp.sundayOT || 0), 0)
          };

          const entry: WeeklyTimesheetEntry = {
            employeeId: dbEmployee?.id || '',
            employeeName,
            hours: totalHours,
            regularHours,
            overtimeHours,
            activities: {
              monday: regularHours.monday > 0 || overtimeHours.monday > 0 ? [{
                hours: regularHours.monday + overtimeHours.monday,
                activityCode: empDataArray[0].activityCode || '',
                activityDescription: empDataArray[0].activityDescription || ''
              }] : [],
              tuesday: regularHours.tuesday > 0 || overtimeHours.tuesday > 0 ? [{
                hours: regularHours.tuesday + overtimeHours.tuesday,
                activityCode: empDataArray[0].activityCode || '',
                activityDescription: empDataArray[0].activityDescription || ''
              }] : [],
              wednesday: regularHours.wednesday > 0 || overtimeHours.wednesday > 0 ? [{
                hours: regularHours.wednesday + overtimeHours.wednesday,
                activityCode: empDataArray[0].activityCode || '',
                activityDescription: empDataArray[0].activityDescription || ''
              }] : [],
              thursday: regularHours.thursday > 0 || overtimeHours.thursday > 0 ? [{
                hours: regularHours.thursday + overtimeHours.thursday,
                activityCode: empDataArray[0].activityCode || '',
                activityDescription: empDataArray[0].activityDescription || ''
              }] : [],
              friday: regularHours.friday > 0 || overtimeHours.friday > 0 ? [{
                hours: regularHours.friday + overtimeHours.friday,
                activityCode: empDataArray[0].activityCode || '',
                activityDescription: empDataArray[0].activityDescription || ''
              }] : [],
              saturday: regularHours.saturday > 0 || overtimeHours.saturday > 0 ? [{
                hours: regularHours.saturday + overtimeHours.saturday,
                activityCode: empDataArray[0].activityCode || '',
                activityDescription: empDataArray[0].activityDescription || ''
              }] : [],
              sunday: regularHours.sunday > 0 || overtimeHours.sunday > 0 ? [{
                hours: regularHours.sunday + overtimeHours.sunday,
                activityCode: empDataArray[0].activityCode || '',
                activityDescription: empDataArray[0].activityDescription || ''
              }] : []
            },
            activityCode: empDataArray[0].activityCode || '',
            activityDescription: empDataArray[0].activityDescription || ''
          };
          
          entries.push(entry);
        }
        
        // Check for existing timesheet for the same week/job
        const existingTimesheet = await findExistingTimesheet(
          timesheetData.jobName,
          timesheetData.jobNumber || '',
          timesheetData.weekEnding
        );

        if (existingTimesheet) {
          // Combine with existing timesheet
          const combinedEntries = combineTimesheetEntries(existingTimesheet.entries, entries);
          
          // Update the existing timesheet
          try {
            await updateDoc(doc(db, 'weeklyTimesheets', existingTimesheet.id), {
              entries: combinedEntries,
              status: 'completed', // Update status
              updatedAt: new Date(),
              // Update contractor info if different
              contractorId: selectedContractor.id,
              contractorName: selectedContractor.name,
              markupPercentage: selectedContractor.markupPercentage
            });
            
            console.log('Successfully combined timesheet with existing ID:', existingTimesheet.id);
            combinedTimesheets.push(existingTimesheet.id);
          } catch (saveError) {
            console.error('Error combining with existing timesheet:', saveError);
            console.error('Existing timesheet ID:', existingTimesheet.id);
            throw saveError;
          }
        } else {
          // Create new timesheet
          const weeklyTimesheet: Omit<WeeklyTimesheet, 'id'> = {
            jobName: timesheetData.jobName,
            ...(timesheetData.jobNumber && { jobNumber: timesheetData.jobNumber }), // Only include if not empty
            weekEnding: timesheetData.weekEnding,
            entries,
            status: 'completed',
            // Add contractor information
            ...(selectedContractor && {
              contractorId: selectedContractor.id,
              contractorName: selectedContractor.name,
              markupPercentage: selectedContractor.markupPercentage
            }),
            createdAt: new Date(),
            updatedAt: new Date(),
            createdBy: currentUser.uid
          };
          
          console.log('About to save new timesheet:', weeklyTimesheet);
          console.log('Week ending date:', timesheetData.weekEnding);
          console.log('Entries count:', entries.length);
          
          try {
            const docRef = await addDoc(collection(db, 'weeklyTimesheets'), weeklyTimesheet);
            console.log('Successfully saved new timesheet with ID:', docRef.id);
            savedTimesheets.push(docRef.id);
          } catch (saveError) {
            console.error('Error saving new timesheet:', saveError);
            console.error('Timesheet data that failed:', weeklyTimesheet);
            throw saveError; // Re-throw to be caught by outer try-catch
          }
        }
      }
      
      // Create success message based on results
      const totalProcessed = savedTimesheets.length + combinedTimesheets.length;
      let successMessage = '';
      
      if (savedTimesheets.length > 0 && combinedTimesheets.length > 0) {
        successMessage = `Successfully processed ${totalProcessed} timesheets: ${savedTimesheets.length} new, ${combinedTimesheets.length} combined with existing`;
      } else if (savedTimesheets.length > 0) {
        successMessage = `Successfully imported ${savedTimesheets.length} new timesheet${savedTimesheets.length > 1 ? 's' : ''}`;
      } else if (combinedTimesheets.length > 0) {
        successMessage = `Successfully combined ${combinedTimesheets.length} timesheet${combinedTimesheets.length > 1 ? 's' : ''} with existing ones`;
      } else {
        successMessage = 'No timesheets were processed';
      }
      
      setSuccess(successMessage);
      
      // Reset state
      setImportedData(null);
      setExcelFile(null);
      setExcelHeaders([]);
      setColumnMapping({
        firstName: '',
        lastName: '',
        employeeName: '',
        employeeCode: '',
        date: '',
        hours: '',
        payType: '',
        activityCode: '',
        activityDescription: '',
        jobName: '',
        jobNumber: '',
        rate: '',
        amount: ''
      });
      
    } catch (err) {
      console.error('Error saving timesheets:', err);
      setError('Failed to save imported timesheets');
    } finally {
      setIsLoading(false);
    }
  };

  const renderSelectField = (label: string, field: keyof typeof columnMapping, isRequired = false) => {
    const isSelected = !!columnMapping[field];
    
    return (
      <div className={`space-y-2 bg-white rounded-lg p-4 border ${isSelected ? 'border-green-300 shadow-md' : 'border-gray-200'} transition-all duration-300`}>
        <div className="flex justify-between items-center">
          <label className="block text-sm font-medium text-gray-700 flex items-center">
            {label}
            {isRequired && <span className="text-red-500 ml-1">*</span>}
            {isSelected && (
              <CheckCircle className="ml-2 h-4 w-4 text-green-600" />
            )}
          </label>
          {columnExamples[columnMapping[field]] && (
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-md">
              Example: "{columnExamples[columnMapping[field]]}"
            </span>
          )}
        </div>
        <select 
          value={columnMapping[field]} 
          onChange={(e) => setColumnMapping({...columnMapping, [field]: e.target.value})}
          className={`w-full border ${isSelected ? 'border-green-300 bg-green-50' : 'border-gray-300'} rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500`}
        >
          <option value="">Select column</option>
          {excelHeaders.map(header => (
            <option key={header} value={header}>
              {header} {columnExamples[header] ? `(e.g., "${columnExamples[header]}")` : ''}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      extractHeaders(acceptedFiles[0]);
    }
  }, [extractHeaders]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    }
  });

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Import Timesheet</h1>
            <p className="text-gray-600 mt-1">Import employee timesheet data from Excel files</p>
          </div>
          <button
            onClick={() => navigate('/timesheets')}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Back to Timesheets
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6 flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">×</button>
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6 flex items-center">
          <CheckCircle className="h-5 w-5 mr-2" />
          {success}
          <button onClick={() => setSuccess(null)} className="ml-auto">×</button>
        </div>
      )}

      {!showMapping && !importedData && (
        <div className="bg-white shadow-xl rounded-xl overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-green-600 to-emerald-700 px-8 py-6 text-white">
            <h2 className="text-2xl font-bold text-center mb-2">Upload Timesheet File</h2>
            <p className="text-center text-green-100">
              Upload an Excel file containing employee timesheet data
            </p>
          </div>
          
          <div className="p-8">
            {/* Import Settings */}
            <div className="mb-6 bg-blue-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Import Settings</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Default Job Name *
                  </label>
                  <input
                    type="text"
                    value={importSettings.jobName}
                    onChange={(e) => {
                      const jobNameValue = e.target.value;
                      
                      // If auto-split is enabled and job number field is empty, automatically split
                      if (autoSplitJobName && jobNameValue && !importSettings.jobNumber) {
                        const { jobNumber: extractedNumber, jobName: extractedName } = splitJobNameNumber(jobNameValue);
                        if (extractedNumber) {
                          setImportSettings(prev => ({
                            ...prev,
                            jobNumber: extractedNumber,
                            jobName: extractedName
                          }));
                          return;
                        }
                      }
                      
                      // Otherwise, just update the job name normally
                      setImportSettings(prev => ({ ...prev, jobName: jobNameValue }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter job/project name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Default Job Number
                  </label>
                  <input
                    type="text"
                    value={importSettings.jobNumber}
                    onChange={(e) => setImportSettings(prev => ({ ...prev, jobNumber: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Optional job number"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="createSeparate"
                    checked={importSettings.createSeparateTimesheets}
                    onChange={(e) => setImportSettings(prev => ({ ...prev, createSeparateTimesheets: e.target.checked }))}
                    className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <label htmlFor="createSeparate" className="ml-2 text-sm text-gray-700">
                    Create separate timesheets per job (recommended for multiple jobs)
                  </label>
                </div>
              </div>
            </div>

            <div 
              {...getRootProps()} 
              className="border-2 border-dashed border-green-300 rounded-xl p-10 text-center cursor-pointer transition-all duration-300 hover:border-green-500 hover:bg-green-50"
            >
              <input {...getInputProps()} />
              <div className="flex flex-col items-center">
                <Upload className={`h-16 w-16 mb-4 ${isDragActive ? 'text-green-600' : 'text-green-400'}`} />
                {isDragActive ? (
                  <p className="text-green-600 text-lg font-medium">Drop the Excel file here...</p>
                ) : (
                  <>
                    <p className="text-gray-700 text-lg font-medium mb-2">Drag and drop a timesheet Excel file here</p>
                    <p className="text-gray-500">or click to select a file</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Column Mapping UI */}
      {showMapping && !isLoading && (
        <div className="mb-8 bg-white shadow-xl rounded-xl overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-8 py-6 text-white">
            <h2 className="text-2xl font-bold text-center mb-2">Map Excel Columns</h2>
            <p className="text-center text-blue-100">
              Match your Excel columns to timesheet fields
            </p>
          </div>
          
          <div className="p-8">
            {/* Week Ending Date Section */}
            <div className="mb-8 bg-blue-50 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Timesheet Week</h3>
              <div className="max-w-md">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Week Ending Date (Sunday) *
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="date"
                    value={importSettings.weekEnding}
                    onChange={(e) => {
                      const selectedDate = e.target.value;
                      const validatedDate = validateWeekEndingDate(selectedDate);
                      setImportSettings(prev => ({ ...prev, weekEnding: validatedDate }));
                    }}
                    className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Week ending date will be automatically adjusted to the next Sunday if needed
                </p>
              </div>
            </div>

            {/* Smart Combining Notice */}
            <div className="mb-8 bg-blue-50 rounded-lg p-6 border border-blue-200">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-blue-900 mb-2">Smart Timesheet Combining</h3>
                  <p className="text-sm text-blue-800">
                    The import process will automatically check for existing timesheets with the same job name, job number, and week ending date. 
                    If found, imported data will be combined with existing timesheets:
                  </p>
                  <ul className="mt-2 text-sm text-blue-700 list-disc pl-5">
                    <li>Employee hours will be added together for existing employees</li>
                    <li>New employees will be added to the existing timesheet</li>
                    <li>Activities will be preserved and combined appropriately</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Contractor Selection */}
            <div className="mb-8 bg-green-50 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Contractor Selection</h3>
              <div className="max-w-md">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Contractor for Burdened Rates *
                </label>
                <select
                  value={selectedContractor?.id || ''}
                  onChange={(e) => {
                    const contractorId = e.target.value;
                    const contractor = contractors.find(c => c.id === contractorId);
                    if (contractor) {
                      setSelectedContractor({
                        id: contractor.id,
                        name: contractor.name || 'Unknown',
                        markupPercentage: contractor.markupPercentage || 4.00
                      });
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                >
                  <option value="">Select a contractor...</option>
                  {contractors.map(contractor => (
                    <option key={contractor.id} value={contractor.id}>
                      {contractor.name} ({contractor.markupPercentage || 4.00}% markup)
                    </option>
                  ))}
                </select>
                {selectedContractor && (
                  <p className="text-sm text-green-600 mt-2">
                    Selected: {selectedContractor.name} ({selectedContractor.markupPercentage}% markup)
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4">Employee Information</h3>
                <div className="space-y-4">
                  {renderSelectField('Employee Name', 'employeeName')}
                  <div className="text-center text-gray-500 text-sm">OR</div>
                  {renderSelectField('First Name', 'firstName')}
                  {renderSelectField('Last Name', 'lastName')}
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4">Time Entry Data</h3>
                <div className="space-y-4">
                  {renderSelectField('Employee Code/ID', 'employeeCode')}
                  {renderSelectField('Date', 'date', true)}
                  {renderSelectField('Hours', 'hours', true)}
                  {renderSelectField('Pay Type (Regular/Overtime)', 'payType', true)}
                  {renderSelectField('Rate ($/hour)', 'rate')}
                  {renderSelectField('Amount (Hours × Rate)', 'amount')}
                </div>
                <div className="mt-4 p-3 bg-blue-50 rounded-md">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> This format expects one row per employee per day.
                    The date field will determine which day of the week the hours belong to.
                    Pay Type should contain "Regular" or "Overtime" to properly categorize hours.
                    <br/><br/>
                    <strong>Rate Comparison:</strong> If Rate and Amount columns are mapped, the system will compare 
                    CEC-provided rates against employee database rates to detect discrepancies.
                  </p>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4">Optional Fields</h3>
                <div className="space-y-4">
                  {renderSelectField('Activity Code', 'activityCode')}
                  {renderSelectField('Activity Description', 'activityDescription')}
                </div>
              </div>
              
              {/* Auto-split Job Name/Number Option */}
              <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="autoSplitJobName"
                      type="checkbox"
                      checked={autoSplitJobName}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        setAutoSplitJobName(isChecked);
                        
                        // Auto-fill job number from job name when auto-split is enabled
                        if (isChecked && importSettings.jobName && !importSettings.jobNumber) {
                          const { jobNumber: extractedNumber, jobName: extractedName } = splitJobNameNumber(importSettings.jobName);
                          if (extractedNumber) {
                            setImportSettings(prev => ({
                              ...prev,
                              jobNumber: extractedNumber,
                              jobName: extractedName
                            }));
                          }
                        }
                      }}
                      className="w-4 h-4 text-amber-600 bg-gray-100 border-gray-300 rounded focus:ring-amber-500 focus:ring-2"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="autoSplitJobName" className="font-medium text-amber-800">
                      Auto-split Combined Job Name/Number Field
                    </label>
                    <p className="text-amber-700 mt-1">
                      Enable this when Job Name contains both job number and name. 
                      The system will automatically extract the job number, separate the job name, and fill the Job Number field above when you type or paste a combined job name.
                    </p>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4">Job Information (Per Row)</h3>
                <div className="space-y-4">
                  {renderSelectField('Job Name (Per Row)', 'jobName', true)}
                  {renderSelectField('Job Number (Per Row)', 'jobNumber')}
                </div>
                <div className="mt-4 p-3 bg-blue-50 rounded-md">
                  <p className="text-sm text-blue-800">
                    <strong>Job Name is required.</strong> If using auto-split, leave Job Number unmapped.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-4 mt-8">
              <button
                onClick={() => {
                  setShowMapping(false);
                  setExcelFile(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Validate required fields
                  let requiredFields = ['date', 'hours', 'payType', 'jobName'];
                  
                  // Check if we have employee identification
                  const hasEmployeeId = columnMapping.employeeName || columnMapping.employeeCode || 
                                      (columnMapping.firstName && columnMapping.lastName) || columnMapping.firstName;
                  
                  if (!hasEmployeeId) {
                    alert('Please map at least one employee identification field (Employee Name, Employee Code, or First Name)');
                    return;
                  }
                  
                  // If auto-split is enabled, jobNumber should not be mapped
                  if (autoSplitJobName && columnMapping.jobNumber) {
                    alert('When using auto-split, please leave the Job Number field unmapped. The system will extract it from Job Name automatically.');
                    return;
                  }
                  
                  // If not using auto-split and no default job info, jobNumber might be required
                  if (!autoSplitJobName && !importSettings.jobNumber && !columnMapping.jobNumber) {
                    // eslint-disable-next-line no-restricted-globals
                    const proceed = confirm('No job number mapping detected. Jobs will be identified by name only. Continue?');
                    if (!proceed) return;
                  }
                  
                  const missingFields = requiredFields.filter(field => !columnMapping[field as keyof typeof columnMapping]);
                  
                  if (missingFields.length > 0) {
                    alert(`Please map the following required fields: ${missingFields.join(', ')}`);
                    return;
                  }
                  
                  processExcelFile();
                }}
                className="inline-flex items-center px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <ArrowRight className="h-4 w-4 mr-2" />
                Process File
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Employee Mapping UI */}
      {showEmployeeMapping && unmatchedEmployees.length > 0 && (
        <div className="bg-white shadow-xl rounded-xl overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-orange-600 to-red-700 px-8 py-6 text-white">
            <h2 className="text-2xl font-bold text-center mb-2">Map Unmatched Employees</h2>
            <p className="text-center text-orange-100">
              {unmatchedEmployees.length} employee{unmatchedEmployees.length > 1 ? 's' : ''} not found in database - map them to existing employees
            </p>
          </div>
          
          <div className="p-8">
            <div className="space-y-4">
              {unmatchedEmployees.map((unmatchedName, index) => {
                const currentMapping = Object.keys(employeeNameMappings).find(key => 
                  key.toLowerCase() === unmatchedName.toLowerCase()
                );
                
                return (
                  <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center space-x-4">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Excel Name: <span className="font-bold text-red-600">{unmatchedName}</span>
                        </label>
                      </div>
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Map to Database Employee:
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="Search employees..."
                            value={employeeSearchTerm}
                            onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 mb-2"
                          />
                          <select
                            value={employeeNameMappings[unmatchedName.toLowerCase()] || ''}
                            onChange={async (e) => {
                              const value = e.target.value;
                              
                              if (value === 'CREATE_ACTIVE' || value === 'CREATE_INACTIVE') {
                                // Open create employee modal
                                const nameParts = unmatchedName.trim().split(' ');
                                const firstName = nameParts[0] || '';
                                const lastName = nameParts.slice(1).join(' ') || '';
                                
                                // Format names properly
                                const formattedFirstName = firstName.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
                                const formattedLastName = lastName.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
                                
                                setCreateEmployeeData({
                                  firstName: formattedFirstName,
                                  lastName: formattedLastName
                                });
                                setShowCreateEmployeeModal(true);
                              } else {
                                const newMappings = {
                                  ...employeeNameMappings,
                                  [unmatchedName.toLowerCase()]: value
                                };
                                if (!value) {
                                  delete newMappings[unmatchedName.toLowerCase()];
                                }
                                setEmployeeNameMappings(newMappings);
                              }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            size={Math.min(8, employees.length + 1)}
                          >
                            <option value="">Select an employee...</option>
                            <option value="CREATE_ACTIVE">✨ Create as Active Employee</option>
                            <option value="CREATE_INACTIVE">✨ Create as Inactive Employee</option>
                            {employees
                              .filter(emp => {
                                if (!employeeSearchTerm) return true;
                                const empName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.name || '';
                                return empName.toLowerCase().includes(employeeSearchTerm.toLowerCase());
                              })
                              .sort((a, b) => {
                                const aName = `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.name || '';
                                const bName = `${b.firstName || ''} ${b.lastName || ''}`.trim() || b.name || '';
                                return aName.localeCompare(bName);
                              })
                              .map(emp => (
                              <option key={emp.id} value={emp.id}>
                                {`${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.name || 'Unknown Employee'}{emp.status === 'inactive' ? ' (Inactive)' : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="flex justify-end space-x-4 mt-8">
              <button
                onClick={() => {
                  setShowEmployeeMapping(false);
                  setUnmatchedEmployees([]);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Skip Mapping
              </button>
              <button
                onClick={() => {
                  saveEmployeeNameMappings(employeeNameMappings);
                  setShowEmployeeMapping(false);
                  setSuccess('Employee mappings saved! Unmatched employees will now show as mapped.');
                }}
                className="inline-flex items-center px-6 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700"
              >
                Save Mappings & Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rate Analysis Display */}
      {showRateAnalysis && rateAnalysis && (
        <div className="bg-white shadow-xl rounded-xl overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-amber-600 to-orange-700 px-8 py-6 text-white">
            <h2 className="text-2xl font-bold text-center mb-2 flex items-center justify-center">
              <TrendingUp className="h-6 w-6 mr-2" />
              Rate Analysis Results
            </h2>
            <p className="text-center text-amber-100">
              {generateRateAnalysisSummary(rateAnalysis)}
            </p>
          </div>
          
          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-blue-900">{rateAnalysis.totalEmployees}</div>
                <div className="text-sm text-blue-700">Total Employees</div>
              </div>
              <div className="bg-red-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-red-900">{rateAnalysis.employeesWithRateDiscrepancies}</div>
                <div className="text-sm text-red-700">Rate Discrepancies</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-green-900">${rateAnalysis.totalRateDiscrepancyAmount.toFixed(2)}</div>
                <div className="text-sm text-green-700">Potential Impact</div>
              </div>
            </div>

            {rateAnalysis.employeeComparisons.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CEC Rate</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Database Rate</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Difference</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {rateAnalysis.employeeComparisons.map((comparison, index) => {
                      const isOvertime = comparison.cecEarningDesc.toLowerCase().includes('overtime') || 
                                        comparison.cecEarningDesc.toLowerCase().includes('ot');
                      const expectedRate = isOvertime ? comparison.databaseOvertimeRate : comparison.databaseRegularRate;
                      
                      return (
                        <tr key={index} className={!comparison.rateMatch ? 'bg-yellow-50' : ''}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {comparison.firstName} {comparison.lastName}
                            </div>
                            <div className="text-xs text-gray-500">
                              {comparison.cecHours.toFixed(1)} hrs | {comparison.cecEarningDesc}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              ${comparison.cecProvidedRate.toFixed(2)}/hr
                            </div>
                            <div className="text-xs text-gray-500">
                              Total: ${comparison.cecAmount.toFixed(2)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {expectedRate ? `$${expectedRate.toFixed(2)}/hr` : 'Not set'}
                            </div>
                            {comparison.databaseRegularRate && comparison.databaseOvertimeRate && (
                              <div className="text-xs text-gray-500">
                                R: ${comparison.databaseRegularRate.toFixed(2)} | 
                                OT: ${comparison.databaseOvertimeRate.toFixed(2)}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {comparison.rateMatch ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Match
                              </span>
                            ) : comparison.rateDifference !== undefined ? (
                              <div>
                                <div className={`text-sm font-medium ${comparison.rateDifference > 0 ? 'text-red-600' : 'text-blue-600'}`}>
                                  {comparison.rateDifference > 0 ? '+' : ''}${comparison.rateDifference.toFixed(2)}
                                </div>
                                {comparison.rateDifferencePercentage !== undefined && (
                                  <div className="text-xs text-gray-500">
                                    ({comparison.rateDifferencePercentage > 0 ? '+' : ''}{comparison.rateDifferencePercentage.toFixed(1)}%)
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-500">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-xs">
                              <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                comparison.recommendedAction === 'no_action' ? 'bg-green-100 text-green-800' :
                                comparison.recommendedAction === 'update_employee_rate' ? 'bg-blue-100 text-blue-800' :
                                comparison.recommendedAction === 'notify_cec' ? 'bg-orange-100 text-orange-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {comparison.recommendedAction.replace('_', ' ')}
                              </div>
                              <div className="text-xs text-gray-600 mt-1">
                                {comparison.actionReason}
                              </div>
                              {!comparison.employeeId && (
                                <div className="mt-1 space-y-1">
                                  <select
                                    onChange={(e) => {
                                      const selectedEmployeeId = e.target.value;
                                      if (selectedEmployeeId) {
                                        // Map this employee name to the selected employee
                                        const employeeName = `${comparison.firstName} ${comparison.lastName}`.toLowerCase();
                                        const newMappings = {
                                          ...employeeNameMappings,
                                          [employeeName]: selectedEmployeeId
                                        };
                                        setEmployeeNameMappings(newMappings);
                                        saveEmployeeNameMappings(newMappings);
                                        setSuccess(`Mapped ${comparison.firstName} ${comparison.lastName} to existing employee`);
                                        
                                        // Refresh the complete rate analysis with the new mapping
                                        if (importedData && selectedContractor && currentUser && rateAnalysis) {
                                          const fullContractor = contractors.find(c => c.id === selectedContractor.id);
                                          if (fullContractor) {
                                            const updatedAnalysis = analyzeCompleteTimesheetRates(
                                              importedData,
                                              employees,
                                              fullContractor,
                                              currentUser.uid,
                                              employeeNameMappings
                                            );
                                            setRateAnalysis(updatedAnalysis);
                                            console.log('RATE COMPARISON: Analysis refreshed after mapping:', updatedAnalysis);
                                          }
                                        }
                                      }
                                    }}
                                    className="w-full px-1 py-1 text-xs border border-gray-300 rounded"
                                  >
                                    <option value="">Map to existing...</option>
                                    {employees
                                      .filter(emp => emp.status === 'active')
                                      .sort((a, b) => {
                                        const aName = `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.name || '';
                                        const bName = `${b.firstName || ''} ${b.lastName || ''}`.trim() || b.name || '';
                                        return aName.localeCompare(bName);
                                      })
                                      .map(emp => (
                                        <option key={emp.id} value={emp.id}>
                                          {`${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.name || 'Unknown Employee'}
                                        </option>
                                      ))
                                    }
                                  </select>
                                  <div className="text-xs text-gray-500 text-center mt-1">
                                    Employee not found in database.<br/>
                                    Use dropdown above to map to existing employee.
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex justify-end space-x-4 mt-8">
              <button
                onClick={() => {
                  setShowRateAnalysis(false);
                  setRateAnalysis(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Close Analysis
              </button>
              <button
                onClick={() => {
                  // Export rate analysis functionality could be added here
                  const analysisData = JSON.stringify(rateAnalysis, null, 2);
                  const blob = new Blob([analysisData], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `rate_analysis_${rateAnalysis.jobName}_${rateAnalysis.weekEnding.toISOString().split('T')[0]}.json`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }}
                className="inline-flex items-center px-6 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700"
              >
                <Download className="h-4 w-4 mr-2" />
                Export Analysis
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Imported Data Preview */}
      {importedData && !isLoading && !showEmployeeMapping && !showRateAnalysis && (
        <div className="bg-white shadow-xl rounded-xl overflow-hidden">
          <div className="bg-gradient-to-r from-green-600 to-emerald-700 px-8 py-6 text-white">
            <h2 className="text-2xl font-bold text-center mb-2">Import Preview</h2>
            <p className="text-center text-green-100">
              Review imported data before saving
            </p>
          </div>
          
          <div className="p-8">
            {importedData.map((timesheet, index) => (
              <div key={index} className="mb-6 border rounded-lg p-4">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">{timesheet.jobName}</h3>
                  {timesheet.jobNumber && (
                    <p className="text-sm text-gray-600">Job #{timesheet.jobNumber}</p>
                  )}
                  <p className="text-sm text-gray-600">
                    Week ending: {timesheet.weekEnding.toLocaleDateString()}
                  </p>
                  <p className="text-sm text-gray-600">
                    Employees: {timesheet.employeeData.length}
                  </p>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mon<br/><small>R/OT</small></th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tue<br/><small>R/OT</small></th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Wed<br/><small>R/OT</small></th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Thu<br/><small>R/OT</small></th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fri<br/><small>R/OT</small></th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sat<br/><small>R/OT</small></th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sun<br/><small>R/OT</small></th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total<br/><small>R/OT</small></th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {timesheet.employeeData.map((emp, empIndex) => {
                        const total = (emp.monday || 0) + (emp.tuesday || 0) + (emp.wednesday || 0) + 
                                     (emp.thursday || 0) + (emp.friday || 0) + (emp.saturday || 0) + (emp.sunday || 0);
                        const totalRegular = (emp.mondayRegular || 0) + (emp.tuesdayRegular || 0) + (emp.wednesdayRegular || 0) + 
                                            (emp.thursdayRegular || 0) + (emp.fridayRegular || 0) + (emp.saturdayRegular || 0) + (emp.sundayRegular || 0);
                        const totalOT = (emp.mondayOT || 0) + (emp.tuesdayOT || 0) + (emp.wednesdayOT || 0) + 
                                       (emp.thursdayOT || 0) + (emp.fridayOT || 0) + (emp.saturdayOT || 0) + (emp.sundayOT || 0);
                        const dbEmployee = matchEmployeeToDatabase(emp.firstName, emp.lastName);
                        
                        const formatHours = (regular: number, ot: number) => {
                          if (regular === 0 && ot === 0) return '-';
                          
                          return (
                            <div className="text-center">
                              <div className="text-sm font-medium text-gray-900">{regular || 0}</div>
                              {ot > 0 && (
                                <div className="text-xs text-orange-600 font-semibold">+{ot} OT</div>
                              )}
                            </div>
                          );
                        };
                        
                        return (
                          <tr key={empIndex} className={!dbEmployee ? 'bg-yellow-50' : ''}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {emp.firstName} {emp.lastName}
                              </div>
                              {!dbEmployee && (
                                <div className="text-xs text-yellow-600">⚠️ Not found in database</div>
                              )}
                              {emp.activityCode && (
                                <div className="text-xs text-gray-500">{emp.activityCode}: {emp.activityDescription}</div>
                              )}
                              {emp.cecRate && emp.cecRate > 0 && (
                                <div className="text-xs text-blue-600">
                                  CEC Rate: ${emp.cecRate.toFixed(2)}/hr ({emp.cecEarningDesc})
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {formatHours(emp.mondayRegular || 0, emp.mondayOT || 0)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {formatHours(emp.tuesdayRegular || 0, emp.tuesdayOT || 0)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {formatHours(emp.wednesdayRegular || 0, emp.wednesdayOT || 0)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {formatHours(emp.thursdayRegular || 0, emp.thursdayOT || 0)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {formatHours(emp.fridayRegular || 0, emp.fridayOT || 0)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {formatHours(emp.saturdayRegular || 0, emp.saturdayOT || 0)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {formatHours(emp.sundayRegular || 0, emp.sundayOT || 0)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <div className="text-sm font-bold text-gray-900">{totalRegular.toFixed(1)}</div>
                              {totalOT > 0 && (
                                <div className="text-xs text-orange-600 font-semibold">+{totalOT.toFixed(1)} OT</div>
                              )}
                              <div className="text-xs text-gray-500 border-t pt-1 mt-1">
                                Total: {total.toFixed(1)}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
            
            <div className="flex justify-between items-center mt-6">
              <div>
                {rateAnalysis && !showRateAnalysis && (
                  <button
                    onClick={() => setShowRateAnalysis(true)}
                    className="inline-flex items-center px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 mr-4"
                  >
                    <TrendingUp className="h-4 w-4 mr-2" />
                    View Rate Analysis
                  </button>
                )}
              </div>
              <div className="flex space-x-4">
                <button
                  onClick={() => {
                    setImportedData(null);
                    setRateAnalysis(null);
                    setShowRateAnalysis(false);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={saveTimesheets}
                  disabled={isLoading}
                  className="inline-flex items-center px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {isLoading ? 'Saving...' : `Save ${importedData.length} Timesheet${importedData.length > 1 ? 's' : ''}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Employee Modal */}
      {showCreateEmployeeModal && createEmployeeData && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Add Employee: {createEmployeeData.firstName} {createEmployeeData.lastName}
            </h3>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.target as HTMLFormElement);
              
              try {
                setIsLoading(true);
                const newEmployee = {
                  name: `${createEmployeeData.firstName} ${createEmployeeData.lastName}`,
                  firstName: createEmployeeData.firstName,
                  lastName: createEmployeeData.lastName,
                  jobTitle: formData.get('jobTitle') as string,
                  hireDate: new Date(formData.get('hireDate') as string),
                  hourlyWage: parseFloat(formData.get('hourlyWage') as string),
                  insurance: parseFloat(formData.get('insurance') as string),
                  vacation: parseFloat(formData.get('vacation') as string),
                  carFuel: parseFloat(formData.get('carFuel') as string || '0'),
                  markup: parseFloat(formData.get('markup') as string || '0'),
                  status: formData.get('status') as string,
                  createdAt: new Date(),
                  updatedAt: new Date(),
                  createdBy: currentUser?.uid || 'system',
                  notes: formData.get('notes') as string || ''
                };

                const docRef = await addDoc(collection(db, 'employees'), newEmployee);
                
                // Add to employees list
                const createdEmployee = { id: docRef.id, ...newEmployee };
                setEmployees(prev => [...prev, createdEmployee]);
                
                setSuccess(`Created employee: ${createEmployeeData.firstName} ${createEmployeeData.lastName}`);
                setShowCreateEmployeeModal(false);
                setCreateEmployeeData(null);
                setIsLoading(false);
                
                // Reload employees list
                await loadEmployees();
              } catch (error) {
                console.error('Error creating employee:', error);
                setError(`Failed to create employee: ${error instanceof Error ? error.message : 'Unknown error'}`);
                setIsLoading(false);
              }
            }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Job Title *</label>
                  <input
                    type="text"
                    name="jobTitle"
                    defaultValue="Electrician"
                    required
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Hire Date *</label>
                  <input
                    type="date"
                    name="hireDate"
                    defaultValue={new Date().toISOString().split('T')[0]}
                    required
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Hourly Wage * ($)</label>
                  <input
                    type="number"
                    name="hourlyWage"
                    step="0.01"
                    min="0"
                    defaultValue="20"
                    required
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Insurance * ($)</label>
                  <input
                    type="number"
                    name="insurance"
                    step="0.01"
                    min="0"
                    defaultValue="1.00"
                    required
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Vacation * ($)</label>
                  <input
                    type="number"
                    name="vacation"
                    step="0.01"
                    min="0"
                    defaultValue="0.80"
                    required
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Car/Fuel ($)</label>
                  <input
                    type="number"
                    name="carFuel"
                    step="0.01"
                    min="0"
                    defaultValue="0"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Markup ($)</label>
                  <input
                    type="number"
                    name="markup"
                    step="0.01"
                    min="0"
                    defaultValue="0"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Status *</label>
                  <select
                    name="status"
                    defaultValue="active"
                    required
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <textarea
                    name="notes"
                    rows={2}
                    defaultValue={`Added from timesheet import on ${new Date().toLocaleDateString()}`}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateEmployeeModal(false);
                    setCreateEmployeeData(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {isLoading ? 'Creating...' : 'Create Employee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="flex justify-center items-center my-8 bg-white p-8 rounded-xl shadow-lg">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
            <span className="text-blue-600 text-lg font-medium">Processing...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimesheetImport;